import * as Yup from "yup";

// Customer Validation Schema
export const customerValidationSchema = Yup.object({
  name: Yup.string()
    .required("Customer name is required")
    .min(2, "Name must be at least 2 characters"),
  email: Yup.string().email("Invalid email format"),
  phone: Yup.string()
    .required("Phone is required")
    .matches(/^[0-9+\-\s()]+$/, "Invalid phone number"),
  address: Yup.string(),
  company_name: Yup.string(),
  dealer_no: Yup.string(),
  tax: Yup.string()
    .required("Tax status is required")
    .oneOf(["Taxable", "Non Taxable"], "Invalid tax status"),
});

// Tax Validation Schema
export const taxValidationSchema = Yup.object({
  tax: Yup.number()
    .required("Tax value is required")
    .min(0, "Tax must be at least 0")
    .max(100, "Tax cannot exceed 100")
    .typeError("Tax must be a number"),
});

export const vendorValidationSchema = Yup.object({
  name: Yup.string()
    .required("Vendor name is required")
    .min(2, "Vendor name must be at least 2 characters"),
  contactPerson: Yup.string().nullable(),
  phone: Yup.string()
    .nullable()
    .matches(/^[0-9+\-\s()]*$/, "Invalid phone number"),
  email: Yup.string().nullable().email("Invalid email format"),
  address: Yup.string().nullable(),
  notes: Yup.string().nullable(),
});

// Service Validation Schema
export const serviceValidationSchema = Yup.object({
  service_name: Yup.string()
    .required("Service name is required")
    .min(2, "Service name must be at least 2 characters"),
  serviceType: Yup.string()
    .required("Service type is required")
    .oneOf(["Standard", "Group"], "Invalid service type"),
  groupCategory: Yup.string().when("serviceType", {
    is: "Group",
    then: (schema) => schema.required("Job category is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  description: Yup.string()
    .required("Description is required")
    .min(5, "Description must be at least 5 characters"),
  price: Yup.number()
    .required("Price is required")
    .positive("Price must be a positive number")
    .typeError("Price must be a valid number"),
  priority: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .integer("Priority must be a whole number")
    .min(1, "Priority must be at least 1")
    .nullable(),
  tax: Yup.string()
    .required("Tax status is required")
    .oneOf(["Taxable", "Non Taxable"], "Invalid tax status"),
});

// Tire Validation Schema
export const tireValidationSchema = Yup.object({
  brand: Yup.string()
    .required("Brand is required")
    .min(2, "Brand must be at least 2 characters"),
  size: Yup.string()
    .required("Size is required")
    .matches(
      /^\d{3}\/\d{2}\/\d{2}$/,
      "Size must be in format ###/##/## (e.g., 245/55/18)",
    ),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(0, "Quantity cannot be negative")
    .integer("Quantity must be a whole number")
    .typeError("Quantity must be a valid number"),
  buyPrice: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .min(0, "Buy price cannot be negative")
    .nullable(),
  price: Yup.number()
    .required("Price is required")
    .positive("Price must be a positive number")
    .typeError("Price must be a valid number"),
  quality: Yup.string()
    .required("Tire type is required")
    .oneOf(["New", "Used"], "Invalid tire type"),
  invoiceNo: Yup.string().nullable(),
  po: Yup.string().nullable(),
  vendor: Yup.string().nullable(),
});

// Receive Stock Validation Schema
export const receiveStockValidationSchema = Yup.object({
  quantity: Yup.number()
    .required("Quantity is required")
    .positive("Quantity must be a positive number")
    .integer("Quantity must be a whole number")
    .min(1, "Quantity must be at least 1")
    .typeError("Quantity must be a valid number"),
  vendor: Yup.string().nullable(),
  invoiceNo: Yup.string().nullable(),
  po: Yup.string().nullable(),
  date: Yup.date().required("Date is required").typeError("Invalid date"),
  note: Yup.string().nullable(),
});

// Part Validation Schema
export const partValidationSchema = Yup.object({
  name: Yup.string().required("Part name is required"),
  partNumber: Yup.string().nullable(),
  description: Yup.string().nullable(),
  quantity: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .min(0, "Quantity cannot be negative")
    .nullable(),
  buyPrice: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .min(0, "Buy price cannot be negative")
    .nullable(),
  sellPrice: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .min(0, "Sell price cannot be negative")
    .required("Sell price is required"),
  vendor: Yup.string().nullable(),
  invoiceNo: Yup.string().nullable(),
  invoiceDate: Yup.date().nullable(),
  partType: Yup.string()
    .oneOf(["Inventory", "Non Inventory"], "Invalid part type")
    .nullable(),
  specialOrderStatus: Yup.string()
    .oneOf(["", "Available", "Assigned", "Sold", "Returned", "Needs Review"], "Invalid special-order status")
    .nullable(),
  refundAmount: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .min(0, "Refund amount cannot be negative")
    .nullable(),
  statusNotes: Yup.string().nullable(),
});

// Oil Validation Schema
export const oilValidationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  brand: Yup.string().required("Brand is required"),
  type: Yup.string().required("Type is required"),
  priority: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .integer("Priority must be a whole number")
    .min(1, "Priority must be at least 1")
    .nullable(),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(0, "Cannot be negative"),
  pricePerQuartz: Yup.number()
    .required("Price per quartz is required")
    .positive("Must be positive"),
  pricePerVehicle: Yup.number()
    .required("Price per vehicle is required")
    .positive("Must be positive"),
});

// Oil Filter Validation Schema
export const filterValidationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  type: Yup.string().required("Type is required"),
  priority: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? undefined : value,
    )
    .integer("Priority must be a whole number")
    .min(1, "Priority must be at least 1")
    .nullable(),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(0, "Cannot be negative"),
  buyPrice: Yup.number()
    .required("Buy price is required")
    .positive("Must be positive"),
  sellPrice: Yup.number()
    .required("Sell price is required")
    .min(0, "Cannot be negative"),
});

// Role validation schema
export const roleValidationSchema = Yup.object({
  name: Yup.string().required("Role name is required"),
});

// Team validation schema
export const teamValidationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string().when("$isEditing", {
    is: false,
    then: (schema) => schema.required("Password is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  user_role: Yup.string().nullable().required("User role is required"),
});

// Vehicle validation schema
export const vehicleValidationSchema = Yup.object({
  customer: Yup.mixed().nullable(),
  no_vin: Yup.boolean().default(false),
  vin_number: Yup.string().when("no_vin", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("VIN number is required"),
  }),
  year: Yup.string().required("Year is required"),
  make: Yup.string().required("Make is required"),
  model: Yup.string().required("Model is required"),
});

//Parts ReceiveStock ValidationSchema
export const PartsReceiveStockValidationSchema = Yup.object({
  quantity: Yup.number()
    .required("Quantity is required")
    .min(1, "Quantity must be at least 1"),
  vendor: Yup.string(),
  invoiceNo: Yup.string(),
  date: Yup.date().required("Date is required"),
  note: Yup.string(),
});
